import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { PageVersionDataService, PageVersionStatusApiModel, PublishDataService, RedeemPageVersionDataService } from '@cxb/editor-stb-api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { createObject, toObject } from 'osn-shared';
import { of, Observable, forkJoin, take } from 'rxjs';
import { map, mapTo, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { RootAppState } from '../app.reducer';
import { selectCxbSelectedTenant } from '../app.selectors';
import { setCxbSuccessNotification } from '../notification/notification.actions';
import * as Action from './page-edit-screen.actions';
import { AppState } from './page-edit-screen.reducer';
import { PageType } from './page-type';
import { PageVersionApiService } from './page-version-api.service';
import { mapRedeemPageVersionDomainToApiModel } from './mapping/map-domain-page-version-to-api';
import { selectCxbCountryCodes, selectCxbUserSegments } from './page-edit-screen.selectors';
import { TrayApiDeleteService } from '../create-tray-screen/tray-api-delete.service';
import { TrayApiCloneService } from '../create-tray-screen/tray-api-clone.service';

@Injectable()
export class PageEditScreenEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly router: Router,
    private readonly pageVersionApiService: PageVersionApiService,
    private readonly pageVersionDataService: PageVersionDataService,
    private readonly redeemPageVersionDataService: RedeemPageVersionDataService,
    private readonly publishDataService: PublishDataService,
    private readonly trayApiDeleteService: TrayApiDeleteService,
    private readonly trayApiCloneService: TrayApiCloneService,
    private readonly store: Store<AppState & RootAppState>
  ) {}

  public loadPage$ = Action.loadCxbPageEditScreen.createEffect$(this.actions$, ({ pageVersionId, userSegments, countryCodes, pageType }) =>
    of(pageVersionId).pipe(
      withLatestFrom(this.store.select(selectCxbSelectedTenant)),
      switchMap(([, selectedTenant]) => {
        if (selectedTenant) {
          return this.pageVersionApiService
            .loadVersionedPage$(pageVersionId, pageType, userSegments, countryCodes, selectedTenant)
            .pipe(toObject('page'));
        }
        return of(null);
      })
    )
  );

  public updatePageVersion$ = Action.updateCxbPageVersion.createEffect$(this.actions$, ({ pageVersion, pageType }) =>
    of(pageVersion).pipe(
      withLatestFrom(this.store.select(selectCxbSelectedTenant)),
      switchMap(([, selectedTenant]) => {
        // sending null value to BE when data is not set
        // when data is set, we have to send both en and ar fields, they cannot be nulls
        const seoTitle =
          pageVersion.seoTitle && (pageVersion.seoTitle.en || pageVersion.seoTitle.ar)
            ? { en: pageVersion.seoTitle.en || '', ar: pageVersion.seoTitle.ar || '' }
            : null;

        const seoDescription =
          pageVersion.seoDescription && (pageVersion.seoDescription.en || pageVersion.seoDescription.ar)
            ? { en: pageVersion.seoDescription.en || '', ar: pageVersion.seoDescription.ar || '' }
            : null;

        return this.pageVersionDataService.updatePageVersion(pageVersion.id, {
          nameLocalized: { en: pageVersion.titleEnglish, ar: pageVersion.titleArabic },
          seoTitle,
          seoDescription,
          internalName: pageVersion.internalName,
          metaTags: pageVersion.metaTags,
          tenantId: selectedTenant?.id,
        }) as Observable<unknown>; // API returns Observable<any> which is worse than unknown
      }),
      map(() => ({ pageVersionId: pageVersion.id, pageType }))
    )
  );

  public updateRedeemPageVersion$ = Action.updateCxbRedeemPageVersion.createEffect$(
    this.actions$,
    ({ pageVersion, countryIds, userSegmentIds }) => {
      return of(pageVersion).pipe(
        withLatestFrom(this.store.select(selectCxbSelectedTenant)),
        switchMap(([, selectedTenant]) =>
          this.redeemPageVersionDataService
            .updateRedeemPageVersion(
              pageVersion.id,
              mapRedeemPageVersionDomainToApiModel(pageVersion, countryIds, userSegmentIds, selectedTenant?.id)
            )
            .pipe(map(() => ({ pageVersionId: pageVersion.id, pageType: PageType.redeem })))
        )
      );
    }
  );

  public updatePageVersionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Action.updateCxbPageVersion.successAction, Action.updateCxbRedeemPageVersion.successAction),
      withLatestFrom(this.store.select(selectCxbSelectedTenant)),
      switchMap(([{ pageVersionId, pageType }, selectedTenant]) => [
        setCxbSuccessNotification({ message: 'Successfully updated the draft page' }),
        Action.loadCxbPageEditScreen.sourceAction({
          pageVersionId,
          userSegments: undefined,
          countryCodes: undefined,
          pageType,
          selectedTenant,
        }),
      ])
    )
  );

  public createPageDraft$ = Action.createCxbPageDraft.createEffect$(this.actions$, ({ pageId, pageType }) => {
    return of(pageId).pipe(
      withLatestFrom(this.store.select(selectCxbSelectedTenant)),
      switchMap(([, selectedTenant]) => {
        return this.publishDataService.createDraftPage(pageId, selectedTenant?.id).pipe(
          switchMap((result) =>
            this.pageVersionApiService
              .loadVersionedPage$(
                result.pageVersions.find(({ status }) => status === PageVersionStatusApiModel.Draft)?.id,
                pageType,
                undefined,
                undefined,
                selectedTenant
              )
              .pipe(take(1))
          ),
          map(({ draftPageId }) => ({ draftPageVersionId: draftPageId, pageType }))
        );
      })
    );
  });

  public createPageDraftSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Action.createCxbPageDraft.successAction),
      tap(({ draftPageVersionId, pageType }) => this.router.navigate([pageType.routePath, draftPageVersionId])),
      mapTo(setCxbSuccessNotification({ message: 'Successfully created draft page' }))
    )
  );

  public publishDraftPage$ = Action.publishCxbPageDraft.createEffect$(this.actions$, ({ pageId, pageType }) =>
    of(pageId).pipe(
      withLatestFrom(this.store.select(selectCxbSelectedTenant)),
      switchMap(([, selectedTenant]) => this.publishDataService.publishPage(pageId, selectedTenant?.id)),
      createObject('pageType', pageType)
    )
  );

  public publishDraftPageSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Action.publishCxbPageDraft.successAction),
      tap(({ pageType }) => {
        this.router.navigate([pageType.rootPath]);
      }),
      map(() => setCxbSuccessNotification({ message: 'Successfully published the draft page' }))
    )
  );

  public deleteDraftPage$ = Action.deleteCxbPageDraft.createEffect$(this.actions$, ({ pageId, pageType }) =>
    of(pageId).pipe(
      withLatestFrom(this.store.select(selectCxbSelectedTenant)),
      switchMap(([, selectedTenant]) => this.publishDataService.discardDraftPage(pageId, selectedTenant?.id)),
      createObject('pageType', pageType)
    )
  );

  public deleteDraftPageSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Action.deleteCxbPageDraft.successAction),
      tap(({ pageType }) => this.router.navigate([pageType.rootPath])),
      mapTo(setCxbSuccessNotification({ message: 'Successfully deleted the draft page' }))
    )
  );

  public unpublishPage$ = Action.unpublishCxbPage.createEffect$(this.actions$, ({ pageId, pageType }) =>
    of(pageId).pipe(
      withLatestFrom(this.store.select(selectCxbSelectedTenant)),
      switchMap(([, selectedTenant]) => this.publishDataService.unPublishPage(pageId, selectedTenant?.id)),
      createObject('pageType', pageType)
    )
  );

  public unpublishPageSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Action.unpublishCxbPage.successAction),
      tap(({ pageType }) => this.router.navigate([pageType.routePath])),
      mapTo(setCxbSuccessNotification({ message: 'Successfully unpublished the page' }))
    )
  );

  public deletePageItem$ = Action.deleteCxbPageItem.createEffect$(this.actions$, ({ id, pageType, trayType, pageVersionId }) =>
    of(id).pipe(
      withLatestFrom(this.store.select(selectCxbSelectedTenant)),
      switchMap(([, selectedTenant]) => this.trayApiDeleteService.deletePageItem$(id, trayType, pageType, selectedTenant)),
      map(() => ({ pageType, pageVersionId }))
    )
  );

  public deletePageItemSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Action.deleteCxbPageItem.successAction),
      withLatestFrom(
        this.store.select(selectCxbUserSegments),
        this.store.select(selectCxbCountryCodes),
        this.store.select(selectCxbSelectedTenant)
      ),
      switchMap(([action, userSegments, countryCodes, selectedTenant]) =>
        of(
          Action.loadCxbPageEditScreen.sourceAction({
            userSegments,
            countryCodes,
            pageVersionId: action.pageVersionId,
            pageType: action.pageType,
            selectedTenant,
          })
        )
      )
    )
  );

  public clonePageItem$ = Action.cloneCxbPageItem.createEffect$(this.actions$, ({ pageVersionId, trayType, id, pageType, targetPageId }) =>
    of(id).pipe(
      withLatestFrom(this.store.select(selectCxbSelectedTenant)),
      switchMap(([, selectedTenant]) => this.trayApiCloneService.clonePageItem$(id, trayType, targetPageId, selectedTenant)),
      map((apiModel) => ({ pageVersionId, id: apiModel.id, trayType, pageType, targetPageId }))
    )
  );

  public clonePageItemSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Action.cloneCxbPageItem.successAction),
      mapTo(setCxbSuccessNotification({ message: 'Successfully created tray item' }))
    )
  );

  public updatePageItems$ = Action.updateCxbPageItems.createEffect$(
    this.actions$,
    (_action) => {
      return of(_action).pipe(
        withLatestFrom(
          this.store.select(selectCxbUserSegments),
          this.store.select(selectCxbCountryCodes),
          this.store.select(selectCxbSelectedTenant)
        ),
        switchMap(([action, userSegments, countryCodes, selectedTenant]) =>
          this.pageVersionDataService.updatePageVersion(action.pageVersionId, { items: action.items, tenantId: selectedTenant?.id }).pipe(
            switchMap(() => {
              return this.pageVersionApiService.loadVersionedPage$(
                action.pageVersionId,
                action.pageType,
                undefined,
                undefined,
                selectedTenant
              );
            }),
            toObject('page')
          )
        )
      );
    },
    (action) =>
      forkJoin([
        this.store.select(selectCxbUserSegments).pipe(take(1)),
        this.store.select(selectCxbCountryCodes).pipe(take(1)),
        this.store.select(selectCxbSelectedTenant).pipe(take(1)),
      ]).pipe(
        map(([userSegments, countryCodes, selectedTenant]) => ({
          pageType: action.pageType,
          pageVersionId: action.pageVersionId,
          userSegments,
          countryCodes,
          selectedTenant,
        }))
      )
  );

  public updatePageItemsFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Action.updateCxbPageItems.failAction),
      withLatestFrom(this.store.select(selectCxbSelectedTenant)),
      switchMap(
        ([
          {
            extras: { userSegments, countryCodes, pageType, pageVersionId },
          },
          selectedTenant,
        ]) => [Action.loadCxbPageEditScreen.sourceAction({ userSegments, countryCodes, pageVersionId, pageType, selectedTenant })]
      )
    )
  );
}
